// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import type { Facility, Project, WorkOrderPaginated } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import type { RequireAtLeastOne } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { BendCreatorView } from 'modules/Shop/EvolveFabrication/BendCreator/BendCreatorView';
import { BendPreviewContextProvider } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';
import { BendCartProvider } from 'modules/Shop/EvolveFabrication/BendCreator/Cart/useBendCart';
import { BendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { BendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { BendBuilderProvider } from 'modules/Shop/EvolveFabrication/BendCreator/State/useBendBuilder';

type Props = {
  /** If provided, will control the Modal directly, hiding the "Bend creator" button */
  opened?: boolean;
  onClose?: () => void;
  onBendsCreated: () => void;
  selectedFacilities?: Facility[];
} & RequireAtLeastOne<{
  workOrder?: WorkOrderPaginated;
  selectedProjects?: Project[];
}>;

export const BendCreatorModal = ({ opened: parentOpened, onClose: parentOnClose, onBendsCreated, ...props }: Props) => {
  const [opened, setOpened] = useUncontrolled({
    value: parentOpened,
    onChange: parentOnClose,
    defaultValue: false,
  });

  const close = useCallback(() => setOpened(false), [setOpened]);

  return (
    <>
      {isNil(parentOpened) && (
        <Button leftSection={<EvolveIcon icon="BendCreator" />} onClick={() => setOpened(true)} variant="gradient">
          Bend creator
        </Button>
      )}

      <WrappedModal
        title="Bend creator"
        opened={opened}
        onClose={close}
        hideModalButtons
        hideHeader
        styles={{ inner: { padding: 20 } }}
        bodyProps={{ p: 0, display: 'flex', flex: 1, style: { flexDirection: 'column' } }}
        fullScreen
      >
        {/* Unmounted while closed, so exiting discards the bend list and the bend being built. */}
        <BendCartProvider>
          <BendCreatorFormContext>
            <BendCreatorValidationContext>
              <BendBuilderProvider>
                <BendPreviewContextProvider>
                  <BendCreatorView
                    onExit={close}
                    onBendsCreated={() => {
                      onBendsCreated();
                      close();
                    }}
                    {...props}
                  />
                </BendPreviewContextProvider>
              </BendBuilderProvider>
            </BendCreatorValidationContext>
          </BendCreatorFormContext>
        </BendCartProvider>
      </WrappedModal>
    </>
  );
};
